import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ordersAPI } from '../api/orders';
import Loader from '../components/common/Loader';
import { ShoppingBagIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';
  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return `${baseUrl}${normalizedPath}`;
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const data = await ordersAPI.getAll();
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'pending':
        return { text: 'Pending', color: 'text-yellow-600' };
      case 'paid':
      case 'processing':
        return { text: 'Processing', color: 'text-gray-600' };
      case 'shipped':
        return { text: 'Shipped', color: 'text-gray-600' };
      case 'delivered':
        return { text: 'Delivered', color: 'text-green-600' };
      case 'cancelled':
        return { text: 'Cancelled', color: 'text-red-500' };
      default:
        return { text: status, color: 'text-gray-500' };
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
      <div className="text-center mb-10">
        <h1 className="text-xl lg:text-2xl font-light tracking-tight text-gray-900">
          Orders
        </h1>
        <div className="w-12 h-px bg-gray-300 mx-auto mt-3"></div>
      </div>
      
      {orders.length === 0 ? (
        <div className="text-center py-16">
          <ShoppingBagIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-sm font-light tracking-wide text-gray-900 mb-2">
            No orders yet
          </h2>
          <p className="text-xs text-gray-400 mb-6">
            You haven't placed any orders.
          </p>
          <Link 
            to="/products" 
            className="inline-flex items-center gap-1 text-xs text-gray-900 border-b border-gray-300 hover:border-gray-900 transition pb-0.5"
          >
            Start shopping
            <ChevronRightIcon className="h-3 w-3" />
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => {
            const statusDisplay = getStatusDisplay(order.status);
            
            return (
              <div key={order.id} className="border border-gray-100">
                {/* Order Header */}
                <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                  <div className="flex flex-wrap justify-between items-center gap-2">
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                        Order #{order.order_number}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-[10px] font-medium uppercase tracking-wider ${statusDisplay.color}`}>
                        {statusDisplay.text}
                      </span>
                      <Link
                        to={`/orders/${order.id}`}
                        className="text-[10px] text-gray-400 hover:text-gray-900 transition uppercase tracking-wider"
                      >
                        View
                      </Link>
                    </div>
                  </div>
                </div>
                
                {/* Order Items */}
                <div className="p-5">
                  <div className="space-y-4">
                    {order.items?.slice(0, 2).map((item, idx) => {
                      const imageUrl = getImageUrl(item.product_image);
                      const hasError = imageErrors[`${order.id}-${idx}`];
                      const itemPrice = item.discount_price || item.price;
                      
                      return (
                        <div key={idx} className="flex gap-4">
                          <div className="w-12 h-14 bg-gray-50 flex-shrink-0 overflow-hidden">
                            {!hasError && imageUrl ? (
                              <img 
                                src={imageUrl} 
                                alt={item.product_name}
                                className="w-full h-full object-cover"
                                onError={() => setImageErrors(prev => ({ ...prev, [`${order.id}-${idx}`]: true }))}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gray-50">
                                <ShoppingBagIcon className="h-5 w-5 text-gray-300" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-900 truncate">{item.product_name}</p>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                              {item.product_size} / {item.product_color}
                            </p>
                            <p className="text-[10px] text-gray-400">Qty: {item.quantity}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-900 whitespace-nowrap">
                              ${((itemPrice || 0) * item.quantity).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    {order.items?.length > 2 && (
                      <p className="text-[10px] text-gray-400 text-center pt-2">
                        +{order.items.length - 2} more item{order.items.length - 2 !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  
                  {/* Order Total */}
                  <div className="border-t border-gray-100 mt-4 pt-4 flex justify-between items-center">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Total</p>
                    <p className="text-sm font-light text-gray-900">
                      ${order.final_amount?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Orders;